import { useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { MEDDPICC_LABELS } from "@/lib/api";
import { 
  Lightbulb, 
  AlertTriangle, 
  CheckCircle2, 
  HelpCircle,
  MessageCircle,
  Target
} from "lucide-react";

export const CoachPanel = ({ interventions, latestFeedback }) => {
  const latestRef = useRef(null);

  useEffect(() => {
    if (latestFeedback && latestRef.current) {
      latestRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [latestFeedback]);

  const getPromptTypeIcon = (type) => {
    switch (type) {
      case "hint":
        return <Lightbulb className="w-4 h-4 text-blue-500" />;
      case "redirect":
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case "reinforcement":
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case "reflection":
        return <HelpCircle className="w-4 h-4 text-purple-500" />;
      case "micro_summary":
        return <Target className="w-4 h-4 text-slate-400" />;
      default:
        return <MessageCircle className="w-4 h-4 text-slate-400" />;
    }
  };

  const getPromptTypeStyle = (type) => {
    switch (type) {
      case "hint":
        return "bg-blue-50/50 border-blue-200 text-blue-900";
      case "redirect":
        return "bg-amber-50/50 border-amber-200 text-amber-900";
      case "reinforcement":
        return "bg-emerald-50/50 border-emerald-200 text-emerald-900";
      case "reflection":
        return "bg-purple-50/50 border-purple-200 text-purple-900";
      case "micro_summary":
        return "bg-slate-50 border-slate-200 text-slate-800";
      default:
        return "bg-slate-50 border-slate-200 text-slate-700";
    }
  };

  const getPromptTypeLabel = (type) => {
    switch (type) {
      case "hint": return "Hint";
      case "redirect": return "Redirect";
      case "reinforcement": return "Well Done!";
      case "reflection": return "Reflect";
      case "micro_summary": return "Summary";
      default: return "Feedback";
    }
  };

  const getSeverityIndicator = (severity) => {
    const dots = [];
    for (let i = 1; i <= 4; i++) {
      dots.push(
        <div 
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${
            i <= severity ? 
              severity <= 1 ? "bg-blue-400" :
              severity === 2 ? "bg-amber-400" :
              severity === 3 ? "bg-orange-400" :
              "bg-red-400"
            : "bg-slate-200"
          }`}
        />
      );
    }
    return <div className="flex gap-0.5">{dots}</div>;
  };

  const sortedInterventions = [...(interventions || [])].sort((a, b) => b.turn_number - a.turn_number);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-orange-500" />
            <h2 className="font-semibold text-sm text-slate-900">Virtual Coach</h2>
          </div>
          {interventions && interventions.length > 0 && (
            <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200">
              {interventions.length} tip{interventions.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Real-time MEDDPICC guidance
        </p>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Latest Feedback Highlight */}
          {latestFeedback && (
            <div ref={latestRef}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
                <span className="text-xs font-semibold text-orange-500 uppercase tracking-wide">
                  Latest Feedback
                </span>
              </div>
              <Card className={`${getPromptTypeStyle(latestFeedback.prompt_type)} border animate-fade-in-up`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {getPromptTypeIcon(latestFeedback.prompt_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {getPromptTypeLabel(latestFeedback.prompt_type)}
                        </Badge>
                        {getSeverityIndicator(latestFeedback.severity)}
                      </div>
                      <p className="text-sm leading-relaxed" data-testid="latest-coach-feedback">
                        {latestFeedback.message}
                      </p>
                      {latestFeedback.meddpicc_dimension && (
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-current/10">
                          <Target className="w-3 h-3" />
                          <span className="text-xs font-medium">
                            {MEDDPICC_LABELS[latestFeedback.meddpicc_dimension]?.name || latestFeedback.meddpicc_dimension}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {sortedInterventions.length > 0 && latestFeedback && (
            <Separator className="bg-slate-100" />
          )}

          {/* Previous Feedback History */}
          {sortedInterventions.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                Coaching History
              </p>
              {sortedInterventions
                .filter(intervention => intervention.id !== latestFeedback?.id)
                .map((intervention, index) => (
                  <div 
                    key={intervention.id || index}
                    className={`p-3 rounded-lg border ${getPromptTypeStyle(intervention.prompt_type)} opacity-75`}
                  >
                    <div className="flex items-start gap-2">
                      {getPromptTypeIcon(intervention.prompt_type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-slate-500">
                            Turn {intervention.turn_number}
                          </span>
                          <Badge variant="outline" className="text-xs scale-90 bg-white/50">
                            {getPromptTypeLabel(intervention.prompt_type)}
                          </Badge>
                        </div>
                        <p className="text-xs leading-relaxed">
                          {intervention.message}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : !latestFeedback ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-7 h-7 text-orange-300" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-1 text-sm">Your Coach is Watching</h3>
              <p className="text-xs text-slate-400 max-w-xs mx-auto leading-relaxed">
                As you progress through the conversation, your virtual coach will provide real-time tips and feedback.
              </p>
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
};
